import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDashboardConfig } from '../../hooks/useDashboardConfig';
import NextJobSummaryOneService from './partials/NextJobSummaryOneService';
import NextJobSummaryUpToFiveServices from './partials/NextJobSummaryUpToFiveServices';
import NextJobSummaryFivePlusServices from './partials/NextJobSummaryFivePlusServices';
import NextJobSummaryServiceStopped from './partials/NextJobSummaryServiceStopped';
import NextJobSummaryNoServices from './partials/NextJobSummaryNoServices';

const NextJobSummary = () => {
  const { servicesEndpoint } = useDashboardConfig();

  const { error, data, isPending } = useQuery({
    queryKey: ['dashboardJobSummary'],
    queryFn: async () => {
      const res = await fetch(servicesEndpoint);
      const json = await res.json();
      return json;
    },
  });

  // Handle reschedule rejected jobs block visibility
  useEffect(() => {
    if (!data) return;

    const blockElements = document.querySelectorAll<HTMLElement>('[data-component="reschedule-rejected-jobs-block"]');
    const shouldShow = data?.totalServices > 1 && data?.weekSummary?.rejectedTotal > 0;

    blockElements.forEach(blockElement => {
      blockElement.classList.toggle('reschedule-jobs-block--visible', shouldShow);
    });

    // Cleanup function runs when component unmounts or dependencies change
    return () => {
      blockElements.forEach(blockElement => {
        blockElement.classList.remove('reschedule-jobs-block--visible');
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.totalServices, data?.weekSummary?.rejectedTotal]);

  if (error) {
    console.error('Error:', error);
  }

  if (isPending) {
    return <NextJobSummarySkeleton />;
  }

  if (!data) {
    return null;
  }

  if (data?.isAccountSuspended) {
    return <NextJobSummaryServiceStopped />;
  }

  if (!data?.totalServices) {
    return <NextJobSummaryNoServices />;
  }

  const componentProps = {
    focusJob: data.focusJob,
    weekSummary: data.weekSummary,
    monthlySummary: data.monthlySummary,
  };

  if (data.totalServices === 1 && data.focusJob) {
    return <NextJobSummaryOneService {...componentProps} />;
  }

  if (data?.totalServices) {
    if (data.totalServices > 5) {
      return <NextJobSummaryFivePlusServices {...componentProps} />;
    }

    return <NextJobSummaryUpToFiveServices {...componentProps} />;
  }
};

function NextJobSummarySkeleton() {
  return (
    <div className="next-job-summary next-job-summary--skeleton">
      <div className="next-job-summary__body">
        <div className="next-job-summary__header">
          <div className="next-job-summary__icon-container">
            <div
              className="skeleton"
              style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', marginBottom: '0', opacity: 0.25 }}
            />
          </div>
          <div className="next-job-summary__header-content">
            <div className="skeleton" style={{ width: '10rem', height: '1.25rem', opacity: 0.25 }} />
          </div>
        </div>
        <div className="next-job-summary__content-card next-job-summary__content-card--skeleton">
          <div className="skeleton" style={{ width: '100%', height: '4rem' }} />
        </div>
        <div className="next-job-summary__action">
          <div className="skeleton" style={{ width: '12rem', height: '1rem', opacity: 0.25 }} />
        </div>
      </div>
    </div>
  );
}

export default NextJobSummary;
